import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  createFileRoute,
  createMemoryHistory,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  notFound,
  RouterProvider,
  useLocation,
} from '@tanstack/react-router';
import { useMemo } from 'react';

import { SettingsLayout } from '#modules/user/features/profile/components/Settings/SettingsLayout';
import { useSettingsSections } from '#modules/user/features/profile/components/Settings/useSettingsSections';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';
import { PageHeading } from '#features/ui';

const SLUGS = [
  'profile',
  'connections',
  'security',
  'notifications',
  'devices',
  'general',
  'blocked',
  'storage',
  'about',
] as const;

/** Real settings navigation in an isolated router: links do not leave the QA page. */
export const Route = createFileRoute('/{-$locale}/_layout/preview/settings-navigation')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    lang: search.lang === 'en' ? ('en' as const) : ('ru' as const),
    section: SLUGS.includes(search.section as (typeof SLUGS)[number]) ? String(search.section) : '',
    verified: search.verified === true || search.verified === 'true',
  }),
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: NavigationPreview,
});

function PreviewContent() {
  const { pathname } = useLocation();
  const sections = useSettingsSections();
  const { t } = useLingui();
  const section = sections.find((entry) => pathname.endsWith(`/${entry.slug}`));
  return (
    <SettingsLayout>
      <PageHeading>{section?.name ?? t`dashboard:settings.title`}</PageHeading>
    </SettingsLayout>
  );
}

function NavigationPreview() {
  const { lang, section, verified } = Route.useSearch();
  const i18n = useMemo(
    () => setupI18n({ locale: lang, messages: { en: enMessages, ru: ruMessages } }),
    [lang]
  );
  const client = useMemo(() => {
    const isolated = new QueryClient();
    const defaults = isolated.defaultQueryOptions.bind(isolated);
    isolated.defaultQueryOptions = (options) => ({
      ...defaults(options),
      enabled: false,
      staleTime: Infinity,
      refetchInterval: false,
    });
    isolated.setQueryData(['me'], {
      __typename: 'CurrentUser',
      id: 'qa-settings-navigation',
      verified,
    });
    return isolated;
  }, [verified]);
  const router = useMemo(() => {
    const root = createRootRouteWithContext<{ isAuth: boolean }>()({ component: PreviewContent });
    const locale = createRoute({ getParentRoute: () => root, path: '{-$locale}' });
    const dashboard = createRoute({ getParentRoute: () => locale, path: 'dashboard' });
    const settings = createRoute({ getParentRoute: () => dashboard, path: 'settings' });
    const children = SLUGS.map((slug) =>
      createRoute({ getParentRoute: () => settings, path: slug })
    );
    return createRouter({
      routeTree: root.addChildren([
        locale.addChildren([dashboard.addChildren([settings.addChildren(children)])]),
      ]),
      context: { isAuth: false },
      history: createMemoryHistory({
        initialEntries: [`/${lang}/dashboard/settings${section ? `/${section}` : ''}`],
      }),
    });
  }, [lang, section]);
  return (
    <I18nProvider i18n={i18n}>
      <QueryClientProvider client={client}>
        <RouterProvider router={router as never} />
      </QueryClientProvider>
    </I18nProvider>
  );
}
